import { useGetModules } from "@/api/permissions/get-modules";
import { useGetPermissionByUserId } from "@/api/permissions/get-permission-by-user-id";
import { useUpdatePermission } from "@/api/permissions/update-permission";
import { useGetMembers } from "@/api/user/get-members";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import useOnboardingData from "@/store/onboarding-data";
import { useEffect, useState } from "react";

type Member = {
    id: string;
    name: string;
    email: string;
    role: string;
};
type Module = {
    id: string;
    name: string;
};

interface Permission {
    id: string;
    user_id: string;
    module: Module;
    can_get_list: boolean;
    can_get_one: boolean;
    can_create: boolean;
    can_update: boolean;
    can_delete: boolean;
    created_at: string;
    updated_at: string;
}

interface CombinedPermission {
    module_id: string;
    module_name: string;
    user_id: string;
    can_get_list: boolean;
    can_get_one: boolean;
    can_create: boolean;
    can_update: boolean;
    can_delete: boolean;
}

type BooleanPermissionKeys = "can_get_list" | "can_get_one" | "can_create" | "can_update" | "can_delete";

function combineModulesAndPermissions(modules: Module[], permissions: Permission[], userId: string): CombinedPermission[] {
    return modules.map((module) => {
        const permission = permissions.find((perm) => perm.module.id === module.id);

        return {
            module_id: module.id,
            module_name: module.name.replace(/_/g, " "),
            user_id: userId, // empty if no permission entry exists
            can_get_list: permission?.can_get_list ?? false,
            can_get_one: permission?.can_get_one ?? false,
            can_create: permission?.can_create ?? false,
            can_update: permission?.can_update ?? false,
            can_delete: permission?.can_delete ?? false,
        };
    });
}

const ManagePermissions = () => {
    const [selectedUserId, setSelectedUserId] = useState<string>();
    const [combinedList, setCombinedList] = useState<CombinedPermission[]>([]);
    const [showSaveButton, setShowSaveButton] = useState<boolean>(false);

    const { data: members, isLoading: isMembersLoading, isError: isMembersError } = useGetMembers();
    const { data: modules, isLoading: isModulesLoading, isError: isModulesError } = useGetModules();
    const { data: userPermission, isLoading: isUserPermissionLoading, isError: isUserPermissionError } = useGetPermissionByUserId(selectedUserId);
    const { mutate } = useUpdatePermission();
    const { userData } = useOnboardingData();

    useEffect(() => {
        if (
            selectedUserId &&
            !isModulesError &&
            !isModulesLoading &&
            !isMembersError &&
            !isMembersLoading &&
            !isUserPermissionError &&
            !isUserPermissionLoading
        ) {
            setCombinedList(combineModulesAndPermissions(modules, userPermission?.data ?? [], selectedUserId ?? ""));
        }
    }, [
        modules,
        userPermission,
        selectedUserId,
        isMembersError,
        isMembersLoading,
        isModulesError,
        isModulesLoading,
        isUserPermissionError,
        isUserPermissionLoading,
    ]);

    const handlePermissionChange = (index: number, key: BooleanPermissionKeys, value: boolean) => {
        const updatedList: CombinedPermission[] = [...combinedList];
        updatedList[index][key] = value;
        setCombinedList(updatedList);
        setShowSaveButton(true);
    };

    const handleSubmit = () => {
        mutate({
            data: combinedList.map((item) => ({
                id: item.module_id,
                module_id: item.module_id,
                user_id: item.user_id,
                can_get_list: item.can_get_list,
                can_get_one: item.can_get_one,
                can_create: item.can_create,
                can_update: item.can_update,
                can_delete: item.can_delete,
            })),
        });
    };

    return (
        <div>
            <Card className="mt-4 p-4 text-gray-700 shadow-none">
                <div className="flex items-center justify-between">
                    <div>
                        <h5 className="text-sm font-bold mb-2">Select Member</h5>
                        {isMembersLoading && <Skeleton className="w-56 h-8 mb-2" />}
                        {!isMembersLoading && (
                            <Select
                                disabled={userData?.role !== "store_owner" && userData?.role !== "super_admin"}
                                value={selectedUserId}
                                onValueChange={(value) => setSelectedUserId(value)}
                            >
                                <SelectTrigger className="w-auto">
                                    <SelectValue placeholder="Select member" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectGroup>
                                        <SelectLabel>Member List</SelectLabel>
                                        {!isMembersLoading &&
                                            !isMembersError &&
                                            members?.data?.map(
                                                (member: Member) =>
                                                    // member?.role !== "store_owner" &&
                                                    member?.role !== "super_admin" && (
                                                        <SelectItem key={member.id} value={member.id}>
                                                            {member.name} ({member.email})
                                                        </SelectItem>
                                                    )
                                            )}
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                        )}
                    </div>
                </div>
                <Separator className="my-4" />
                {isModulesLoading && (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Module Name</TableHead>
                                <TableHead className="text-center">Read Single</TableHead>
                                <TableHead className="text-center">Read List</TableHead>
                                <TableHead className="text-center">Write</TableHead>
                                <TableHead className="text-center">Update</TableHead>
                                <TableHead className="text-center">Delete</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]?.map((item: number) => (
                                <TableRow key={item}>
                                    <TableCell className="font-medium">
                                        <Skeleton className="w-56 h-8 mb-2" />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Skeleton className="w-56 h-8 mb-2" />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Skeleton className="w-56 h-8 mb-2" />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Skeleton className="w-56 h-8 mb-2" />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Skeleton className="w-56 h-8 mb-2" />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Skeleton className="w-56 h-8 mb-2" />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
                {!isModulesError && !isMembersLoading && selectedUserId && (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Module Name</TableHead>
                                <TableHead className="text-center">Read Single</TableHead>
                                <TableHead className="text-center">Read List</TableHead>
                                <TableHead className="text-center">Write</TableHead>
                                <TableHead className="text-center">Update</TableHead>
                                <TableHead className="text-center">Delete</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {combinedList?.map((module: CombinedPermission, index: number) => (
                                <TableRow key={module?.module_id}>
                                    <TableCell className="font-medium">{module?.module_name}</TableCell>
                                    <TableCell align="center">
                                        <Checkbox
                                            id="terms"
                                            checked={module?.can_get_one}
                                            onCheckedChange={(value) => handlePermissionChange(index, "can_get_one", value as boolean)}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Checkbox
                                            id="terms"
                                            checked={module?.can_get_list}
                                            onCheckedChange={(value) => handlePermissionChange(index, "can_get_list", value as boolean)}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Checkbox
                                            id="terms"
                                            checked={module?.can_create}
                                            onCheckedChange={(value) => handlePermissionChange(index, "can_create", value as boolean)}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Checkbox
                                            id="terms"
                                            checked={module?.can_update}
                                            onCheckedChange={(value) => handlePermissionChange(index, "can_update", value as boolean)}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Checkbox
                                            id="terms"
                                            checked={module?.can_delete}
                                            onCheckedChange={(value) => handlePermissionChange(index, "can_delete", value as boolean)}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
                {showSaveButton && (
                    <>
                        <Separator className="my-4" />
                        <div className="flex justify-end">
                            <Button onClick={handleSubmit} className="cursor-pointer">
                                Save
                            </Button>
                        </div>
                    </>
                )}
            </Card>
        </div>
    );
};

export default ManagePermissions;
